import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import Swal from 'sweetalert2';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { LeaveDialogComponent, LeaveDialogData, LeaveDialogResult } from './leave-dialog/leave-dialog.component';
import { LeaveRules, leaveRulesOf } from '../hr-leave-rights';
import { LeaveClosingWindow, leaveClosingWindowOf, leavePeriodShortLabel } from '../hr-leave-closing';
import {
	HR_ABSENCE_STATE_LABELS, HrAbsence, HrClock, HrCounter, HrEmployee, HrLeavePeriod, HrOverride, HrSchedule, HrTeam,
	hrEmployeeNameOf, hrIsHrManager, hrMyEmployeeId,
} from '@app/shared/_models/hr';
import { pairClocks } from '../hr-timesheet';
import { hrCountedLeaveDays, hrGuessRestWeekdays } from '../hr-leave';
import { EffectiveSlot, effectiveSlotsForRange, frenchHolidaysOf } from '../hr-schedule-engine';
import { absenceQuotaAccountOf, absenceQuotaIssue, absenceQuotaOf } from '../hr-absence-quota';
import { PayrollRules, hrPayrollRulesOf } from '../hr-payroll';
import { employeeWorkProfileOf } from '../hr-worktime';
import { buildCounter } from '../hr-counter';
import { RcrAccount, rcrAbsenceTypes, rcrAccountOf, rcrLabel } from '../hr-rcr';
import {
	LEAVE_VIEWS, LeaveGroup, LeaveRibbon, LeaveTime, LeaveView, MonthTick, RibbonSegment,
	compareLeaveRows, leaveGroupsOf, leaveRibbonOf, leaveSegmentOf, leaveTimeOf, leaveViewMatches, monthGridCss,
	monthTicksOf, normalizeSearch, yearPositionOf,
} from '../hr-leave-board';
import { todayStr, wallMinutesOf } from '@app/shared/time';
import { C_value, Store, User } from '@app/shared/_models/common';
import {
	C_VALUES_HR_ABSENCE_TYPE, C_VALUES_HR_TEAMS,
	EVENTTYP_HR_ABSENCE_DECIDE, EVENTTYP_INSERT, EVENTTYP_UPDATE,
	HR_ABSENCE_STATE_APPROVED, HR_ABSENCE_STATE_CANCELLED, HR_ABSENCE_STATE_REFUSED, HR_ABSENCE_STATE_REQUESTED,
	HR_LEAVE_PERIOD_CLOSED, HR_LEAVE_PERIOD_PROPOSED,
	OBJECTTYP_HR_ABSENCE,
} from '@app/shared/constants';

// Les libellés vivent désormais dans _models/hr.ts (partagés avec la fiche employé) — réexportés
// ici pour les consommateurs historiques.
export { HR_ABSENCE_STATE_LABELS };

interface LeaveRow {
	absence: HrAbsence;
	id: number;
	id_employee: number;
	datestart: string;
	employee: string;
	store: string;					// établissement de rattachement — sans lui, 400 noms se ressemblent
	typ: string;
	typColor: string;
	period: string;
	days: number;
	state: number;
	stateLabel: string;
	time: LeaveTime;				// où la période se situe par rapport à aujourd'hui
	ribbon: LeaveRibbon;		// sa place sur la réglette des douze mois
	// La même place, sous la forme que rend la réglette. Une seule barre ici, autant que d'absences
	// en mode groupé : le gabarit de rendu est alors le même pour les deux modes.
	segments: RibbonSegment[];
	comment: string;
	haystack: string;				// nom + type + commentaire, normalisés une fois pour la recherche
	canDecide: boolean;		// admin + en attente
	canCancel: boolean;		// demandeur (ou admin) + en attente
	canRequalify: boolean;	// admin + en attente : retoucher plutôt que trancher
	// Absence EN COURS (fin inconnue) : elle attend sa clôture — par le responsable, ou par le
	// salarié lui-même quand il reçoit son arrêt de travail.
	isOpen: boolean;
	canClose: boolean;
	// Combien de boutons la ligne porte réellement. C'est ce nombre — le plus gros de la liste
	// affichée — qui donne sa largeur à la colonne des actions, au lieu d'une réserve fixe.
	actionCount: number;
}

// ============================================================
// Le gabarit du tableau — largeurs calées sur du contenu MESURÉ
// ============================================================
//
// L'écran est prévu pour 1920 px, et doit tenir sans rien tronquer dès 1880. À cette largeur le
// tableau dispose de ~1833 px : les colonnes fixes en prennent 648, la réglette 30 %, et tout le
// reste — plus de 600 px — va au commentaire.
//
// La colonne des ACTIONS n'existe que s'il y a quelque chose à y faire. Réservée en permanence,
// elle plantait une bande blanche de 140 px à droite du commentaire : sur une liste d'absences
// passées — où plus rien ne se décide — le texte s'abrégeait en « … » à 140 px du bord de l'écran,
// devant du vide. Elle réapparaît, à la largeur exacte de ses boutons, dès qu'une ligne en porte.
const LEAVE_COLUMNS = ['employee', 'typ', 'period', 'ribbon', 'state', 'comment', 'actions'];
const LEAVE_COLUMNS_READONLY = ['employee', 'typ', 'period', 'ribbon', 'state', 'comment'];

// Un `mat-icon-button` mesure 28 px sous le thème de l'application (`mat.theme(density: -5)`) et
// les boutons se touchent ; la cellule ajoute ses marges (8 px à gauche, 12 px à droite pour la
// dernière colonne). Cinq boutons au maximum : approuver, refuser, requalifier, clôturer, annuler.
const LEAVE_ACTION_BUTTON_PX = 28;
const LEAVE_ACTION_PADDING_PX = 20;

// Congés & absences — /hr/leaves. Demande par l'employé, validation/refus par le patron (rôles 0/1).
@Component({
	selector: 'app-hr-leaves',
	standalone: true,
	imports: [RouterLink, NgTemplateOutlet, MenuCommonComponent, ReactiveFormsModule, MatTableModule, MatFormFieldModule,
		MatInputModule, MatSelectModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatTooltipModule,
		MatPaginatorModule, MatSortModule, OverlayModule],
	templateUrl: './leaves.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './leaves.component.scss',
})
export class LeavesComponent implements OnInit, OnDestroy {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private dialog = inject(MatDialog);
	private overlay = inject(Overlay);
	private viewContainerRef = inject(ViewContainerRef);

	// La PÉRIODE est coupée en deux colonnes : le texte (largeur fixe, la même pour un jour comme
	// pour un mois) et la réglette (largeur fixe elle aussi, sinon les segments de deux lignes ne
	// s'alignent pas — et l'alignement vertical est tout l'intérêt de la chose).
	public displayedColumns: string[] = LEAVE_COLUMNS_READONLY;
	public groupedColumns: string[] = ['employee', 'summary', 'ribbon'];
	// Largeur de la colonne des actions, en pixels : le nombre de boutons de la ligne la plus
	// chargée, et rien de plus. Le commentaire récupère tout ce qu'elle ne prend pas.
	public actionsWidth = 0;
	public dataSource: MatTableDataSource<LeaveRow> = new MatTableDataSource<LeaveRow>();
	public groupSource: MatTableDataSource<LeaveGroup> = new MatTableDataSource<LeaveGroup>();

	// Deux tableaux mutuellement exclusifs, donc deux jeux de pagination et de tri : le passage par
	// des setters les rebranche à chaque bascule de mode, là où un @ViewChild lu une seule fois en
	// ngAfterViewInit laisserait le second tableau sans pagination.
	@ViewChild('detailPager') set detailPager(p: MatPaginator | undefined) { this.dataSource.paginator = p ?? null; }
	@ViewChild('groupPager') set groupPager(p: MatPaginator | undefined) { this.groupSource.paginator = p ?? null; }
	@ViewChild('detailSort') set detailSort(s: MatSort | undefined) { this.dataSource.sort = s ?? null; }
	@ViewChild('groupSort') set groupSort(s: MatSort | undefined) { this.groupSource.sort = s ?? null; }
	@ViewChild('groupPanel') groupPanelTpl?: TemplateRef<unknown>;

	public year: number = +todayStr().slice(0, 4);		// année MÉTIER, pas celle de l'horloge du poste
	public isAdmin = false;
	public stateLabels = HR_ABSENCE_STATE_LABELS;

	// ============================================================
	// FILTRES — deux étages
	// ============================================================
	//
	// LA VUE dit ce qu'on est venu faire : traiter des demandes, savoir qui est absent aujourd'hui,
	// consulter l'historique. Elle est toujours visible, avec son compte : « À traiter (3) » est à
	// la fois un filtre et une alerte, et le zéro rassure autant que le trois appelle.
	//
	// LE PÉRIMÈTRE dit de QUI l'on parle : établissement, équipe, motif, nom. Sur un effectif de
	// quelques personnes il ne sert à rien ; sur quatre cents il est la seule façon de retrouver
	// une ligne. Réservé aux habilités RH — un salarié ne voit que ses propres demandes.
	public viewCtrl = new FormControl<LeaveView>('all', { nonNullable: true });
	public storeCtrl = new FormControl<number | null>(null);
	public teamCtrl = new FormControl<number | null>(null);
	// Les MOTIFS se cochent, ils ne se choisissent pas un par un : « maladie et accident du
	// travail, sans les congés payés » est la question qu'on se pose vraiment, et elle ne tient
	// pas dans une liste à choix unique. Liste vide = tous les motifs.
	public typeCtrl = new FormControl<number[]>([], { nonNullable: true });
	public searchCtrl = new FormControl<string>('', { nonNullable: true });

	public readonly views = LEAVE_VIEWS;
	public counts: Record<LeaveView, number> = { todo: 0, current: 0, upcoming: 0, past: 0, closed: 0, all: 0 };
	public shownDays = 0;

	// ============================================================
	// MODE GROUPÉ — une personne, une ligne
	// ============================================================
	//
	// La liste détaillée répond à « quelles demandes dois-je traiter ». Elle ne répond pas à « qui
	// s'absente, et quand » : les douze congés d'un même salarié y sont dispersés sur douze lignes.
	// Groupé, chacun tient sur une seule réglette — et le texte des périodes, qui ne peut plus y
	// figurer, passe dans le panneau de survol.
	public groupCtrl = new FormControl<boolean>(false, { nonNullable: true });
	public get grouped(): boolean { return this.groupCtrl.value; }

	public stores: Store[] = [];
	public teams: HrTeam[] = [];

	// Réglette annuelle : repères de mois et quadrillage, recalculés au changement d'année.
	public monthTicks: MonthTick[] = monthTicksOf(this.year);
	public monthGrid: string = monthGridCss(this.year);
	public todayPct: number | null = null;

	private employees: HrEmployee[] = [];
	private users: User[] = [];
	private types: C_value[] = [];
	private absences: HrAbsence[] = [];
	private allRows: LeaveRow[] = [];		// toutes les lignes VISIBLES par l'utilisateur, avant filtres
	// Repos hebdomadaires PROPOSÉS dans le dialog : les jours où la personne n'a aucun créneau
	// théorique, tous emplois du temps confondus.
	// Règles de congés de l'établissement (c_values 25) : période de prise, fractionnement.
	public leaveRules: LeaveRules = leaveRulesOf(null);
	private restByEmployee = new Map<number, number[]>();

	// CLÔTURE DE L'ANNÉE DE CONGÉS — le rappel de campagne.
	//
	// L'écran des congés est celui qu'on ouvre pour poser des jours ; c'est donc là qu'il faut dire
	// « il vous reste 6 jours sur l'année qui s'achève ». Le détail et le formulaire vivent sur
	// /hr/leave-closing : ici, on ne fait qu'appeler, avec le chiffre qui donne envie de cliquer.
	public closingWindow: LeaveClosingWindow | null = null;
	public closingTodo = 0;					// comptes non arrêtés (responsable RH)
	public myClosingToAck: HrLeavePeriod | null = null;	// MON solde en attente de mon accord

	// RÉCUPÉRATION (repos compensateur posé comme un congé) : le compte de chaque salarié
	// concerné, les types d'absence qui le débitent, et le planning THÉORIQUE qui les valorise.
	public payrollRules: PayrollRules = hrPayrollRulesOf(null);
	public rcrTypes: number[] = [];
	private rcrAccounts = new Map<number, RcrAccount>();
	private plannedSlots: EffectiveSlot[] = [];

	// Index de rendu — construits une fois par chargement. `find` dans une boucle de lignes coûte
	// le produit des deux tailles : sur 400 salariés et quelques milliers d'absences, c'est la
	// différence entre un écran instantané et un écran qui rame.
	private employeeById = new Map<number, HrEmployee>();
	private storeNameById = new Map<number, string>();
	private typeById = new Map<number, C_value>();

	ngOnInit(): void {
		// Tout filtre qui bouge refait la liste — jamais le chargement : rebuild() ne fait que
		// retrancher parmi des lignes déjà calculées. La bascule groupé/détaillé passe par le même
		// chemin : les deux tableaux sortent des MÊMES lignes filtrées, donc « À traiter » groupé
		// montre les salariés dont une demande attend, et rien d'autre.
		const filters: { valueChanges: Observable<unknown> }[] =
			[this.viewCtrl, this.storeCtrl, this.teamCtrl, this.typeCtrl, this.searchCtrl, this.groupCtrl];
		for (const ctrl of filters) ctrl.valueChanges.subscribe(() => this.rebuild());
		// Ce que trie chaque en-tête. « Période » trie sur la DATE DE DÉBUT, jamais sur le texte
		// affiché : « 09/07 » précède « 27/06 » dans l'ordre alphabétique, et un tri par période
		// qui range juillet avant juin ne serait pas un tri par période.
		this.dataSource.sortingDataAccessor = (row: LeaveRow, id: string) => {
			switch (id) {
				case 'employee': return row.employee;
				case 'typ': return row.typ;
				case 'period': return row.datestart;
				case 'state': return row.state;
				default: return '';
			}
		};
		// Groupé, il n'y a plus ni type ni période à trier : ce qui distingue deux lignes, c'est le
		// nom et le VOLUME d'absence — « qui s'absente le plus » est la question de ce mode.
		this.groupSource.sortingDataAccessor = (group: LeaveGroup, id: string) => {
			switch (id) {
				case 'employee': return group.employee;
				case 'summary': return group.days;
				default: return '';
			}
		};
		this.load();
	}

	ngOnDestroy(): void {
		this.closePanel();
	}

	private load(): void {
		forkJoin({
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores().pipe(catchError(() => of([] as Store[]))),
			// Référentiels c_values : valuesOf$ porte les deux pièges du forkJoin (flux qui ne
			// complète jamais, échec qui fige l'écran) — cf. hr.service.ts.
			types: this.hrService.valuesOf$(C_VALUES_HR_ABSENCE_TYPE),
			payrollRules: this.hrService.payrollRules$(),
			// Équipes (c_values 28) : groupes TRANSVERSES de salariés, mêmes que ceux du planning.
			teams: this.hrService.valuesOf$(C_VALUES_HR_TEAMS),
			absences: this.hrService.getAbsences$(this.year + '-01-01', this.year + '-12-31'),
			schedules: this.hrService.getSchedules$(),
			// Compteur de récupération : il faut le planning THÉORIQUE (exceptions comprises) pour
			// chiffrer ce qu'une demande retire, et les ajustements manuels pour le solde acquis.
			overrides: this.hrService.getOverrides$(this.year + '-01-01', this.year + '-12-31').pipe(catchError(() => of([] as HrOverride[]))),
			counters: this.hrService.getCounters$(this.year + '-01-01', this.year + '-12-31').pipe(catchError(() => of([] as HrCounter[]))),
			// Les pointages ne servent qu'aux établissements où la POINTEUSE fait foi : sans eux, le
			// compteur y verrait zéro heure travaillée, donc zéro heure supplémentaire à récupérer.
			clocks: this.hrService.getClocks$(this.year + '-01-01', this.year + '-12-31').pipe(catchError(() => of([] as HrClock[]))),
			// Clôtures de l'année de congés — pour le rappel de campagne en tête d'écran.
			closings: this.hrService.getLeavePeriods$().pipe(catchError(() => of([] as HrLeavePeriod[]))),
		}).subscribe(({ employees, users, stores, types, teams, absences, schedules, payrollRules, overrides, counters, clocks, closings }) => {
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);		// la base fait foi (vieux JWT sans roles)
			this.payrollRules = payrollRules.rules;
			this.leaveRules = payrollRules.leave;
			this.employees = employees;
			this.users = users;
			this.stores = (stores ?? []) as Store[];
			this.types = types as C_value[];
			this.teams = ((teams ?? []) as C_value[]).filter(t => t.is_active !== false).map(t => HrTeam.fromValue(t));
			this.absences = absences;
			this.buildIndexes();
			this.buildRestGuesses(schedules);
			this.buildRcrAccounts(schedules, overrides, counters, clocks);
			this.buildClosingReminder(closings);
			this.buildRows();
			this.rebuild();
		});
	}

	// Les tables de correspondance du rendu, montées une fois par chargement.
	private buildIndexes(): void {
		this.employeeById = new Map(this.employees.map(e => [e.id, e]));
		this.storeNameById = new Map(this.stores.filter(s => s.id != null).map(s => [s.id!, s.name ?? ('Établissement #' + s.id)]));
		this.typeById = new Map(this.types.filter(t => t.id != null).map(t => [t.id!, t]));
	}

	// Le rappel de campagne : ce qui reste à faire, et rien d'autre. Le calcul complet des soldes
	// est l'affaire de /hr/leave-closing — le refaire ici alourdirait un écran qu'on ouvre vingt
	// fois par jour pour poser trois jours de congés.
	private buildClosingReminder(closings: HrLeavePeriod[]): void {
		this.closingTodo = 0;
		this.myClosingToAck = null;
		if (!this.leaveRules.closing_enabled) { this.closingWindow = null; return; }

		const w = leaveClosingWindowOf(todayStr(), this.leaveRules);
		this.closingWindow = w;
		const forPeriod = closings.filter(c => c.datstart === w.period.from);

		if (this.isAdmin) {
			const closed = new Set(forPeriod.filter(c => c.state === HR_LEAVE_PERIOD_CLOSED).map(c => c.id_employee));
			this.closingTodo = this.employees.filter(e => e.is_active !== false && !closed.has(e.id)).length;
		}
		const myId = this.myEmployeeId();
		if (myId != null && this.leaveRules.closing_employee_ack) {
			this.myClosingToAck = forPeriod.find(c => c.id_employee === myId && c.state === HR_LEAVE_PERIOD_PROPOSED) ?? null;
		}
	}

	public readonly leavePeriodShortLabel = leavePeriodShortLabel;

	// ============================================================
	// RÉCUPÉRATION — le stock d'heures supplémentaires posables en congé
	// ============================================================
	//
	// Le planning est projeté SANS les absences : c'est le planning THÉORIQUE qu'une demande
	// efface, et une journée déjà masquée par la demande en cours vaudrait sinon zéro heure.
	//
	// LES DEUX PROJECTIONS SONT HORS DE LA BOUCLE. Elles ne dépendent pas du salarié : les refaire
	// pour chacun, c'est projeter l'année entière quatre cents fois pour en lire un quatre-centième
	// — l'écran mettait alors des minutes à s'ouvrir sur un effectif réel.
	private buildRcrAccounts(schedules: HrSchedule[], overrides: HrOverride[], counters: HrCounter[], clocks: HrClock[]): void {
		this.rcrTypes = rcrAbsenceTypes(this.types);
		this.rcrAccounts = new Map();
		if (this.rcrTypes.length === 0) { this.plannedSlots = []; return; }

		const from = this.year + '-01-01';
		const to = this.year + '-12-31';
		this.plannedSlots = effectiveSlotsForRange(from, to, schedules, overrides, []);
		const countedSlots = effectiveSlotsForRange(from, to, schedules, overrides, this.absences);

		// Un seul compte à calculer pour un salarié, tous pour un responsable.
		const myId = this.myEmployeeId();
		const targets = this.isAdmin
			? this.employees.filter(e => e.is_active !== false)
			: this.employees.filter(e => e.id === myId);

		// Tout ce que la boucle consommait par `filter` est indexé UNE fois : sans cela, chaque
		// salarié relisait l'intégralité des créneaux, des pointages et des absences.
		const plannedBy = groupBy(this.plannedSlots, s => s.id_employee);
		const countedBy = groupBy(countedSlots, s => s.id_employee);
		const periodsBy = groupBy(pairClocks(clocks, todayStr()).periods, p => p.id_employee);
		const countersBy = groupBy(counters, c => c.id_employee);
		const absencesBy = this.absencesByEmployee();
		const schedulesById = new Map(schedules.map(s => [s.id, s]));

		for (const emp of targets) {
			const sch = (emp.id_schedule != null ? schedulesById.get(emp.id_schedule) : null) ?? null;
			// Le CRÉDIT acquis vient du compteur d'heures — une seule source, sinon deux écrans
			// finissent par annoncer deux soldes différents du même stock.
			const [cell] = buildCounter({
				employee: emp,
				slots: countedBy.get(emp.id) ?? [],
				periods: periodsBy.get(emp.id) ?? [],
				absences: absencesBy.get(emp.id) ?? [], absenceTypes: this.types,
				adjustments: (countersBy.get(emp.id) ?? []).map(c => ({ dat: c.dat, minutes: c.minutes, kind: c.kind })),
				anchorMonday: sch?.anchor_monday ?? null,
				cycleWeeks: sch?.cycle_weeks ?? 1,
				from, to, granularity: 'year',
				source: this.payrollRules.time_source,
				rules: this.payrollRules,
				profile: employeeWorkProfileOf(emp.data),
				today: todayStr(),
				now_min: wallMinutesOf(new Date()) ?? 24 * 60,
			});
			this.rcrAccounts.set(emp.id, rcrAccountOf(
				cell?.rcr_credited_min ?? 0, absencesBy.get(emp.id) ?? [],
				this.rcrTypes, plannedBy.get(emp.id) ?? [],
			));
		}
	}

	// Les absences de chacun, pour le contrôle des congés PLAFONNÉS (un jour d'enfant malade par
	// an…). L'écran ne charge que l'année affichée : un droit calé sur la période de référence des
	// congés payés (juin → mai) n'y voit donc qu'une partie de sa période, et sous-estime au pire
	// ce qui a déjà été pris. Le responsable garde la main, et le contrôle reste un garde-fou.
	private absencesByEmployee(): Map<number, HrAbsence[]> {
		const byEmployee = groupBy(this.absences, a => a.id_employee);
		// Un salarié sans aucune absence doit quand même avoir son compte : sinon le dialog croirait
		// la donnée absente et n'afficherait aucun droit.
		for (const e of this.employees) {
			if (!byEmployee.has(e.id)) byEmployee.set(e.id, []);
		}
		return byEmployee;
	}

	// Le compte du salarié connecté — la carte affichée en tête d'écran.
	get myRcrAccount(): RcrAccount | null {
		const id = this.myEmployeeId();
		return id != null ? (this.rcrAccounts.get(id) ?? null) : null;
	}

	public rcrLabel = rcrLabel;

	// Un salarié peut être planifié dans PLUSIEURS services : ses jours travaillés sont l'union
	// de tous ses créneaux, ses repos le complément.
	private buildRestGuesses(schedules: HrSchedule[]): void {
		const slotsByEmployee = new Map<number, { weekday: number }[]>();
		for (const sch of schedules) {
			// Grille terminée = rythme périmé : on garde les grilles courantes ET futures (une nouvelle
			// grille à venir donne déjà les bons repos), on écarte seulement celles déjà closes.
			if (sch.is_active === false || (sch.datend != null && sch.datend < todayStr())) continue;
			for (const slot of (sch.lnk_slots ?? [])) {
				if (!slotsByEmployee.has(slot.id_employee)) slotsByEmployee.set(slot.id_employee, []);
				slotsByEmployee.get(slot.id_employee)!.push({ weekday: slot.weekday });
			}
		}
		this.restByEmployee = new Map(
			this.employees.map(e => [e.id, hrGuessRestWeekdays(slotsByEmployee.get(e.id) ?? [])]),
		);
	}

	// ============================================================
	// Lignes, vues, périmètre
	// ============================================================

	// Les lignes VISIBLES par l'utilisateur, calculées une fois par chargement : les filtres qui
	// suivent ne font plus que retrancher, si bien qu'une frappe dans la recherche ne recalcule
	// jamais ni les réglettes ni les droits.
	private buildRows(): void {
		const myEmployeeId = this.myEmployeeId();
		const today = todayStr();
		this.todayPct = this.pctInYear(today);
		this.monthTicks = monthTicksOf(this.year);
		this.monthGrid = monthGridCss(this.year);
		this.allRows = this.absences
			// Hors responsable RH : SES demandes, et rien d'autre. Le serveur ne renvoie déjà que
			// celles-là (hr/routes.rs) ; le filtre ici garde l'écran juste même si une liste plus
			// large lui arrivait un jour (cache, autre appelant, serveur non déployé).
			.filter(a => this.isAdmin || a.id_employee === myEmployeeId)
			.map(a => this.toRow(a, myEmployeeId, today));
	}

	// Le trait « aujourd'hui » sur la réglette — absent quand on consulte une autre année.
	private pctInYear(dat: string): number | null {
		const pct = yearPositionOf(dat, this.year);
		return pct != null && pct >= 0 && pct <= 100 ? pct : null;
	}

	private rebuild(): void {
		const inScope = this.scopePredicate();
		const scoped = this.allRows.filter(inScope);

		// Les comptes des vues portent sur le PÉRIMÈTRE, pas sur l'écran : « À traiter (3) » doit
		// dire trois demandes chez CE salarié de CET établissement, sinon le chiffre ne se rapporte
		// à rien de ce qu'on regarde.
		this.counts = { todo: 0, current: 0, upcoming: 0, past: 0, closed: 0, all: scoped.length };
		for (const r of scoped) {
			for (const v of LEAVE_VIEWS) {
				if (v.value !== 'all' && leaveViewMatches(v.value, r.state, r.time)) this.counts[v.value]++;
			}
		}

		const view = this.viewCtrl.value;
		const rows = scoped.filter(r => leaveViewMatches(view, r.state, r.time)).sort(compareLeaveRows);
		this.shownDays = rows.reduce((total, r) => total + r.days, 0);

		// La colonne des actions se dimensionne sur ce qu'elle porte vraiment. Le calcul se fait sur
		// la liste FILTRÉE, pas sur la page affichée : une colonne qui change de largeur en tournant
		// les pages ferait danser le tableau sous les yeux, pour gagner quelques pixels.
		const buttons = rows.reduce((most, r) => Math.max(most, r.actionCount), 0);
		this.actionsWidth = buttons * LEAVE_ACTION_BUTTON_PX + LEAVE_ACTION_PADDING_PX;
		this.displayedColumns = buttons > 0 ? LEAVE_COLUMNS : LEAVE_COLUMNS_READONLY;

		this.dataSource.data = rows;
		// Le panneau de survol décrit un groupe qui vient d'être refait : il parlerait d'un état
		// périmé, et son ancrage a disparu du DOM.
		this.closePanel();
		this.groupSource.data = this.grouped ? leaveGroupsOf(rows) : [];
		this.dataSource.paginator?.firstPage();
		this.groupSource.paginator?.firstPage();
	}

	// Le tableau affiché est vide — la question ne se pose pas de la même façon selon le mode.
	public get isEmpty(): boolean {
		return (this.grouped ? this.groupSource.data.length : this.dataSource.data.length) === 0;
	}

	// Établissement, équipe, motifs, nom : le périmètre, indépendant de la vue.
	//
	// Le prédicat est monté UNE fois par filtrage, pas une fois par ligne : chercher l'équipe et
	// normaliser la recherche à chaque ligne referait le même travail des milliers de fois à
	// chaque frappe, pour un résultat qui ne change pas d'une ligne à l'autre.
	private scopePredicate(): (row: LeaveRow) => boolean {
		const store = this.storeCtrl.value;
		const members = this.teamCtrl.value != null
			? new Set(this.teams.find(t => t.id === this.teamCtrl.value)?.members ?? [])
			: null;
		const types = this.typeCtrl.value.length > 0 ? new Set(this.typeCtrl.value) : null;
		const needle = normalizeSearch(this.searchCtrl.value);

		return (r: LeaveRow) => {
			const id = r.absence.id_employee;
			if (store != null && this.employeeById.get(id)?.id_store !== store) return false;
			if (members && !members.has(id)) return false;
			if (types && (r.absence.typ == null || !types.has(r.absence.typ))) return false;
			return needle === '' || r.haystack.includes(needle);
		};
	}

	public get hasScopeFilter(): boolean {
		return this.storeCtrl.value != null || this.teamCtrl.value != null
			|| this.typeCtrl.value.length > 0 || this.searchCtrl.value.trim() !== '';
	}

	onResetFilters(): void {
		this.storeCtrl.setValue(null, { emitEvent: false });
		this.teamCtrl.setValue(null, { emitEvent: false });
		this.typeCtrl.setValue([], { emitEvent: false });
		this.searchCtrl.setValue('', { emitEvent: false });
		this.rebuild();
	}

	// Les motifs réellement utilisés cette année-là : proposer les douze types du référentiel quand
	// trois servent, c'est faire chercher dans une liste où rien ne manque et où tout est inutile.
	public get typeChoices(): C_value[] {
		const used = new Set(this.allRows.map(r => r.absence.typ));
		return this.types.filter(t => t.id != null && used.has(t.id));
	}

	private myEmployeeId(): number | null {
		return hrMyEmployeeId(this.loginService.loggeduser, this.employees);
	}

	// ============================================================
	// PANNEAU DE SURVOL — ce que la ligne groupée ne peut pas écrire
	// ============================================================
	//
	// Une ligne groupée montre QUAND, jamais QUOI : douze segments n'ont pas la place de porter
	// leurs dates, leur motif et leur état. Le survol de la réglette rend cette information en
	// toutes lettres, sans faire quitter l'écran ni changer de mode.
	//
	// Un vrai panneau flottant (cdk Overlay), pas une infobulle : le contenu est une liste avec ses
	// puces de couleur et ses états, et elle doit rester consultable — donc survolable et
	// défilable — quand un salarié cumule trente absences dans l'année.
	public hoverGroup: LeaveGroup | null = null;
	private panelRef: OverlayRef | null = null;
	private panelTimer: ReturnType<typeof setTimeout> | null = null;

	// Le délai évite d'ouvrir un panneau à chaque réglette balayée en descendant la liste.
	private static readonly PANEL_HOVER_DELAY_MS = 140;
	private static readonly PANEL_CLOSE_GRACE_MS = 180;

	onGroupEnter(ev: MouseEvent, group: LeaveGroup): void {
		this.cancelPanelTimer();
		const anchor = ev.currentTarget as HTMLElement | null;
		if (!anchor) return;
		// Déjà ouvert sur ce groupe : le survol d'un segment voisin ne doit pas le refermer.
		if (this.panelRef && this.hoverGroup?.id_employee === group.id_employee) return;
		this.panelTimer = setTimeout(() => this.openPanel(anchor, group), LeavesComponent.PANEL_HOVER_DELAY_MS);
	}

	// Fermeture DIFFÉRÉE : le délai de grâce laisse traverser les quelques pixels qui séparent la
	// réglette du panneau, sans quoi il se refermerait avant d'être atteint.
	onGroupLeave(): void {
		this.cancelPanelTimer();
		this.panelTimer = setTimeout(() => this.closePanel(), LeavesComponent.PANEL_CLOSE_GRACE_MS);
	}

	onPanelEnter(): void {
		this.cancelPanelTimer();
	}

	private openPanel(anchor: HTMLElement, group: LeaveGroup): void {
		this.closePanel();
		if (!this.groupPanelTpl) return;		// template absent (tests à gabarit vide)
		this.hoverGroup = group;
		this.panelRef = this.overlay.create({
			positionStrategy: this.overlay.position()
				.flexibleConnectedTo(anchor)
				.withPositions([
					{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
					{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
					{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
					{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
				])
				.withPush(true)
				.withViewportMargin(8),
			// La liste défile sous le panneau : il suit son ancrage plutôt que de rester en l'air.
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
		});
		this.panelRef.attach(new TemplatePortal(this.groupPanelTpl, this.viewContainerRef));
	}

	private closePanel(): void {
		this.cancelPanelTimer();
		this.panelRef?.dispose();
		this.panelRef = null;
		this.hoverGroup = null;
	}

	private cancelPanelTimer(): void {
		if (this.panelTimer) { clearTimeout(this.panelTimer); this.panelTimer = null; }
	}

	private toRow(a: HrAbsence, myEmployeeId: number | null, today: string): LeaveRow {
		const typ = a.typ != null ? this.typeById.get(a.typ) : undefined;
		const employee = this.employeeById.get(a.id_employee);
		const pending = a.state === HR_ABSENCE_STATE_REQUESTED;
		const isOpen = a.open === true;
		const name = hrEmployeeNameOf(employee, this.users);
		const typName = typ?.name ?? ('Type #' + a.typ);
		const comment = a.comment ?? '';
		const store = employee?.id_store != null ? (this.storeNameById.get(employee.id_store) ?? '') : '';
		const ribbon = leaveRibbonOf({ datestart: a.datestart, dateend: a.dateend, open: isOpen }, this.year, today);
		const canDecide = this.isAdmin && pending;
		const canCancel = pending && (this.isAdmin || a.id_employee === myEmployeeId);
		const canRequalify = this.isAdmin && pending;
		// Le salarié clôture la SIENNE : c'est lui qui reçoit l'arrêt de travail, et le faire passer
		// par un tiers ne ferait qu'ajouter un délai. Le serveur la remet alors en attente — le
		// responsable revalide ce qu'il n'a pas saisi.
		const canClose = isOpen && (this.isAdmin || a.id_employee === myEmployeeId);
		return {
			absence: a,
			id: a.id,
			id_employee: a.id_employee,
			datestart: a.datestart,
			employee: name,
			store,
			typ: typName,
			typColor: typ?.data?.color ?? '#9e9e9e',
			// Une absence en cours n'a pas de fin à afficher : « depuis le 24/07 », et c'est tout.
			// Écrire une date de fin qu'on ne connaît pas serait la seule vraie erreur ici.
			period: isOpen
				? 'depuis le ' + this.fmt(a.datestart)
				: this.fmt(a.datestart) + (a.dateend !== a.datestart ? ' → ' + this.fmt(a.dateend) : ''),
			days: HrAbsence.daysInclusive(a.datestart, a.dateend),
			state: a.state,
			stateLabel: HR_ABSENCE_STATE_LABELS[a.state] ?? ('État ' + a.state),
			time: leaveTimeOf({ datestart: a.datestart, dateend: a.dateend, open: isOpen }, today),
			ribbon,
			segments: [leaveSegmentOf({ id: a.id, ribbon, typColor: typ?.data?.color ?? '#9e9e9e', state: a.state, isOpen })],
			comment,
			haystack: normalizeSearch(name + ' ' + store + ' ' + typName + ' ' + comment),
			canDecide,
			canCancel,
			canRequalify,
			isOpen,
			canClose,
			// « Approuver » et « Refuser » vont par deux : c'est la même décision, prise dans un sens
			// ou dans l'autre, et elles s'affichent ensemble.
			actionCount: (canDecide ? 2 : 0) + (canRequalify ? 1 : 0) + (canClose ? 1 : 0) + (canCancel ? 1 : 0),
		};
	}

	private fmt(dat: string): string {
		return dat ? (dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4)) : '';
	}

	onYearShift(delta: number): void {
		this.year += delta;
		this.load();
	}

	// ============================================================
	// Nouvelle demande
	// ============================================================

	onNew(): void {
		const myId = this.myEmployeeId();
		const candidates = (this.isAdmin ? this.employees.filter(e => e.is_active !== false) : this.employees.filter(e => e.id === myId))
			.map(e => ({ id: e.id, name: hrEmployeeNameOf(e, this.users) }))
			.sort((a, b) => a.name.localeCompare(b.name));
		if (candidates.length === 0) {
			this.alertService.error(this.isAdmin ? 'Aucune fiche employé.' : 'Aucune fiche employé ne correspond à votre compte — demandez sa création.');
			return;
		}
		this.openLeaveDialog(candidates, myId ?? candidates[0].id, null);
	}

	// ============================================================
	// Requalification — la troisième réponse du responsable
	// ============================================================
	//
	// « Approuver » ou « Refuser » suppose que la demande est bien qualifiée. Ce qui arrive
	// vraiment, c'est une absence injustifiée qui doit devenir un congé payé, ou deux jours
	// d'enfant malade là où la convention n'en accorde qu'un. Refuser sèchement oblige le salarié
	// à ressaisir ce que le responsable a déjà sous les yeux : on retouche, et on tranche.
	//
	// La demande est encore EN ATTENTE, donc encore modifiable (garde serveur : state 0) — et
	// c'est bien la demande du salarié qu'on corrige, jamais son auteur : l'employé ne change pas.
	onRequalify(row: LeaveRow): void {
		const a = row.absence;
		const employee = this.employees.find(e => e.id === a.id_employee);
		this.openLeaveDialog(
			[{ id: a.id_employee, name: hrEmployeeNameOf(employee, this.users) }],
			a.id_employee, a,
		);
	}

	// ============================================================
	// Absence CONSTATÉE — « ce matin, il n'est pas là »
	// ============================================================
	//
	// Le responsable ne fait pas une demande : il note un fait, et il le note AVANT de savoir de
	// quoi il s'agit. La fin viendra avec l'arrêt de travail, le motif définitif aussi — d'où une
	// absence approuvée d'emblée et laissée OUVERTE, que la clôture viendra fermer.
	onConstat(): void {
		const candidates = this.employees.filter(e => e.is_active !== false)
			.map(e => ({ id: e.id, name: hrEmployeeNameOf(e, this.users) }))
			.sort((a, b) => a.name.localeCompare(b.name));
		if (candidates.length === 0) { this.alertService.error('Aucune fiche employé.'); return; }
		this.openLeaveDialog(candidates, candidates[0].id, null, true);
	}

	private openLeaveDialog(
		candidates: { id: number; name: string }[],
		defaultEmployee: number,
		absence: HrAbsence | null,
		constat = false,
	): void {
		const data: LeaveDialogData = {
			employees: candidates, types: this.types,
			id_employee_default: defaultEmployee, isAdmin: this.isAdmin,
			restWeekdaysByEmployee: this.restByEmployee,
			leaveRules: this.leaveRules,
			rcrAccountByEmployee: this.rcrAccountsExcluding(absence),
			plannedSlots: this.plannedSlots,
			// Seul le congé PAYÉ ouvre droit au fractionnement : une maladie posée en février
			// n'accorde évidemment aucun jour supplémentaire.
			paidLeaveTypes: this.types.filter(t => t.data?.paid_leave === true).map(t => t.id ?? 0),
			// Congés PLAFONNÉS : ce que chacun a déjà posé, et depuis quand il est là.
			absencesByEmployee: this.absencesByEmployee(),
			hireDateByEmployee: new Map(this.employees.map(e => [e.id, e.hire_date ?? null])),
			absence: absence ?? undefined,
			constat,
		};
		this.dialog.open(LeaveDialogComponent, { data, width: '520px' }).afterClosed().subscribe((result: LeaveDialogResult) => {
			if (!result) return;
			if (result.id > 0 && absence) this.submitRequalification(result, absence);
			else this.submitNew(result);
		});
	}

	// SAISIE DU RESPONSABLE RH — UN SEUL EVENT, la ligne naît approuvée (state 1).
	//
	// L'aller-retour « demande puis décision » n'avait rien à raconter ici : même auteur, même
	// seconde, et le responsable ne s'adresse pas une demande à lui-même. Il déposait en prime une
	// notification « X demande une absence » à destination des responsables — donc à lui — et
	// faisait clignoter la ligne dans « À traiter » le temps du second appel. Le serveur stampe le
	// décideur à l'INSERT : la piste d'audit dit qui a validé, sans le détour.
	//
	// Le SALARIÉ, lui, demande toujours : state 0, et un responsable tranchera.
	private submitNew(result: LeaveDialogResult): void {
		const direct = result.approveNow;
		const payload = this.hrService.buildAbsencePayload({
			...result, id: 0, state: direct ? HR_ABSENCE_STATE_APPROVED : HR_ABSENCE_STATE_REQUESTED,
		});
		this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_ABSENCE, payload).subscribe({
			next: () => { this.alertService.success(direct ? 'Absence enregistrée' : 'Demande enregistrée'); this.load(); },
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	// La demande retouchée reste EN ATTENTE le temps de l'UPDATE, puis la décision suit si le
	// responsable l'a demandée — mêmes deux temps qu'à la création, même trace dans c_event.
	//
	// CLÔTURE d'une absence déjà approuvée : aucune décision à enchaîner. Le serveur la laisse
	// approuvée (responsable) ou la remet en attente (salarié qui complète la sienne), et l'event
	// 2701 refuserait de toute façon de décider une seconde fois d'une absence déjà décidée.
	private submitRequalification(result: LeaveDialogResult, original: HrAbsence): void {
		// Le `data` d'origine est conservé (renonciation aux jours de fractionnement, qui vaut
		// accord individuel du salarié) SAUF `half`, que la durée requalifiée redonne : une
		// demi-journée devenue journée entière laisserait sinon une moitié fantôme au planning.
		const { half: _half, ...keptData } = (original.data ?? {}) as Record<string, any>;
		const wasPending = original.state === HR_ABSENCE_STATE_REQUESTED;
		const closing = original.open === true;
		const payload = this.hrService.buildAbsencePayload({
			...result, id: original.id, state: HR_ABSENCE_STATE_REQUESTED, data: keptData,
		});
		this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_ABSENCE, payload).pipe(
			switchMap(res => (result.approveNow && wasPending)
				? this.hrService.postHrEvent(EVENTTYP_HR_ABSENCE_DECIDE, OBJECTTYP_HR_ABSENCE,
					{ id: original.id, state: HR_ABSENCE_STATE_APPROVED, comment: null })
				: of(res)),
		).subscribe({
			next: () => {
				this.alertService.success(closing && !result.open
					? 'Absence clôturée'
					: (result.approveNow && wasPending ? 'Demande requalifiée et approuvée' : 'Demande requalifiée'));
				this.load();
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	// Le compte de récupération SANS la demande qu'on requalifie : encore en attente, elle est
	// déjà comptée dans `pending_min` et s'opposerait à sa propre correction.
	private rcrAccountsExcluding(absence: HrAbsence | null): Map<number, RcrAccount> {
		const base = absence ? this.rcrAccounts.get(absence.id_employee) : null;
		if (!absence || !base) return this.rcrAccounts;
		const accounts = new Map(this.rcrAccounts);
		accounts.set(absence.id_employee, rcrAccountOf(
			base.credited_min,
			this.absences.filter(a => a.id_employee === absence.id_employee && a.id !== absence.id),
			this.rcrTypes,
			this.plannedSlots.filter(s => s.id_employee === absence.id_employee),
		));
		return accounts;
	}

	// ============================================================
	// Décision (admin) et annulation
	// ============================================================

	onApprove(row: LeaveRow): void {
		// Le plafond s'oppose AUSSI à l'approbation : le garde-fou de saisie ne couvre pas les
		// lignes entrées ailleurs (fiche employé, reprise de données) ni celles d'avant le
		// compteur. Le responsable garde le dernier mot — mais il l'a en connaissance de cause.
		const issue = this.quotaIssueOf(row.absence);
		Swal.fire({
			title: issue ? 'Plafond dépassé — approuver quand même ?' : 'Approuver cette absence ?',
			text: row.employee + ' — ' + row.typ + ' — ' + row.period
				+ (issue ? '\n\n' + issue + '\nRequalifiez plutôt la demande pour la ramener dans le droit.' : ''),
			icon: issue ? 'warning' : 'question',
			showCancelButton: true, confirmButtonText: 'Approuver', cancelButtonText: 'Annuler',
		}).then(r => {
			if (r.isConfirmed) this.decide(row, HR_ABSENCE_STATE_APPROVED, null);
		});
	}

	// Le motif de refus qu'oppose le plafond à une absence DÉJÀ EN BASE, ou null si elle y tient.
	// Elle s'exclut d'elle-même du compte : encore en attente, elle se compterait sinon comme
	// obstacle à sa propre approbation.
	private quotaIssueOf(a: HrAbsence): string | null {
		const type = this.types.find(t => t.id === a.typ);
		const quota = absenceQuotaOf(type);
		if (!quota) return null;

		const year = +a.datestart.slice(0, 4);
		const holidays = new Map<string, string>();
		for (let y = year - 1; y <= year + 1; y++) {
			for (const [k, label] of frenchHolidaysOf(y)) holidays.set(k, label);
		}
		const account = absenceQuotaAccountOf(
			quota,
			this.absences.filter(x => x.id_employee === a.id_employee && x.typ === a.typ && x.id !== a.id),
			a.datestart,
			this.employees.find(e => e.id === a.id_employee)?.hire_date ?? null,
			this.leaveRules, holidays,
		);
		const cost = hrCountedLeaveDays([a], account.period.from, account.period.to, holidays)
			.reduce((total, d) => total + d.days, 0);
		return absenceQuotaIssue(cost, account, type?.name);
	}

	onRefuse(row: LeaveRow): void {
		Swal.fire({
			title: 'Refuser cette absence ?',
			text: row.employee + ' — ' + row.typ + ' — ' + row.period,
			input: 'text', inputPlaceholder: 'Motif du refus (facultatif)',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Refuser', cancelButtonText: 'Annuler',
		}).then(r => {
			if (r.isConfirmed) this.decide(row, HR_ABSENCE_STATE_REFUSED, (r.value ?? '').toString().trim() || null);
		});
	}

	private decide(row: LeaveRow, state: number, comment: string | null): void {
		this.hrService.postHrEvent(EVENTTYP_HR_ABSENCE_DECIDE, OBJECTTYP_HR_ABSENCE, { id: row.absence.id, state, comment }).subscribe({
			next: () => { this.alertService.success(state === HR_ABSENCE_STATE_APPROVED ? 'Absence approuvée' : 'Absence refusée'); this.load(); },
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	onCancel(row: LeaveRow): void {
		Swal.fire({
			title: 'Annuler cette demande ?',
			icon: 'question', showCancelButton: true, confirmButtonText: 'Oui, annuler', cancelButtonText: 'Non',
		}).then(r => {
			if (!r.isConfirmed) return;
			const payload = this.hrService.buildAbsencePayload({ ...row.absence, state: HR_ABSENCE_STATE_CANCELLED });
			this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_ABSENCE, payload).subscribe({
				next: () => { this.alertService.success('Demande annulée'); this.load(); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}
}

// Regroupement en un passage — le remplaçant de `filter` dans une boucle.
function groupBy<T>(items: T[], key: (item: T) => number): Map<number, T[]> {
	const map = new Map<number, T[]>();
	for (const item of items) {
		const k = key(item);
		const list = map.get(k);
		if (list) list.push(item); else map.set(k, [item]);
	}
	return map;
}
